/**
 * ArticleV2ScreenFlowCoordinator - V2 coordinator for article draft screens
 *
 * Fork of StrategyV2ScreenFlowCoordinator adapted for the article context:
 * - Constructor: (projectId, articleId) instead of (projectId, strategyId, blockId)
 * - Persistence: the article draft's INLINE flowDocument via
 *   ArticleDraftService.saveFlowDocument — there is no per-screen subcollection,
 *   so every mutation converges on ONE whole-document write rebuilt from the
 *   registry and pushed through a serialized save queue (whole-doc storage is
 *   last-write-wins; unserialized concurrent saves would drop screens).
 * - Launch screen: flowDocument.launchScreenId (no separate parent doc)
 * - Variables: flowDocument.variables via the FlowVariablesHost capability
 *   (articles have no onboardingId/versionId for flowVariablesService)
 * - screensGraphMetadata: flowDocument.metadata (kind:'article' reaches the
 *   screen editor so article-only chrome — profile chips, container presets —
 *   activates)
 *
 * Reuses: v2ScreenRegistry, v2ScreenAdapter, v2TransitionManager (all stateless/clearable singletons)
 */

import type { FlowDocument, FunnelVariable, StepNode } from '@segmently/schema-models/v2';
import type { ReactFlowCanvasHandle, EntityMove, PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';
import type {
  FlowCoordinator,
  CreateEntityResult,
  CreateTransitionInput,
  LoadingCallbacks,
  FlowVariablesHost,
} from '../shared/FlowCoordinator';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import { v2ScreenRegistry } from '../onboarding/V2ScreenRegistry';
import { v2ScreenAdapter, type FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { v2TransitionManager } from '../onboarding/V2TransitionManager';
import { clearAllStepEdges } from '@modules/reactFlow/utils/transformers/V2EdgeCleaner';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import { buildV2ActionHandles } from '@modules/reactFlow/services/shared/V2ActionHandleResolver';
import { ArticleDraftService, type ArticleDraft } from '@modules/firebase-core/services/ArticleDraftService';

interface EdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  label: string;
  type: 'transition';
  animated: boolean;
  data: {
    sourceEntityId: string;
    targetEntityId: string;
    sourceActionKey: string;
    rules?: unknown[];
    onEdgeClick?: (edgeId: string, edgeData: any) => void;
  };
}

/**
 * ArticleV2ScreenFlowCoordinator
 *
 * Implements: FlowCoordinator<FlowStepNode, ArticleDraftService> + FlowVariablesHost
 */
export class ArticleV2ScreenFlowCoordinator
implements FlowCoordinator<FlowStepNode, ArticleDraftService>, FlowVariablesHost {
  private initialized: boolean = false;
  private draftService: ArticleDraftService | null = null;
  private projectId: string;
  private articleId: string;
  private draft: ArticleDraft | null = null;
  private canvasHandle: ReactFlowCanvasHandle | null = null;
  private loadingCallbacks?: LoadingCallbacks;
  private launchScreenId: string | null = null;
  private onLaunchScreenChanged?: (newLaunchScreenId: string) => void;
  private variables: Record<string, FunnelVariable> = {};

  /**
   * Serialized save queue. Each queued save rebuilds the FULL flowDocument from
   * the registry at execution time, so a burst of mutations converges on the
   * complete final state even when an intermediate write fails (the failure
   * rejects only its own caller; the next save carries everything).
   */
  private saveTail: Promise<void> = Promise.resolve();

  /**
   * Document-level metadata from flowDocument.metadata (kind: 'article',
   * articlePresentation, alias…). The canvas editor threads it into the
   * assembled FlowDocument so article-only editor features gate on it.
   */
  public screensGraphMetadata: Record<string, unknown> | null = null;

  private templateSource: ((templateId: string) => StepNode | null) | null = null;
  public targetLocales: string[] = [];
  public defaultLanguage: string = 'en';

  constructor(projectId: string, articleId: string, draftService?: ArticleDraftService) {
    this.projectId = projectId;
    this.articleId = articleId;
    this.draftService = draftService ?? new ArticleDraftService();
    this.initialized = true;
    console.log('[ArticleV2Coordinator] Initialized:', { projectId, articleId });
  }

  // ============================================================================
  // GETTERS
  // ============================================================================

  public getProjectId(): string { return this.projectId; }
  public getArticleId(): string { return this.articleId; }
  public getDraft(): ArticleDraft | null { return this.draft; }

  public setTemplateSource(getter: (templateId: string) => StepNode | null): void {
    this.templateSource = getter;
  }

  public setLocaleSettings(targetLocales: string[], defaultLanguage: string): void {
    this.targetLocales = targetLocales;
    this.defaultLanguage = defaultLanguage;
  }

  public setOnLaunchScreenChanged(callback: (newLaunchScreenId: string) => void): void {
    this.onLaunchScreenChanged = callback;
  }

  // ============================================================================
  // INITIALIZATION & LIFECYCLE
  // ============================================================================

  async loadScreensFromFirebase(): Promise<void> {
    if (!this.draftService) {
      console.warn('[ArticleV2Coordinator] Cannot load screens - service not initialized');
      return;
    }

    const draft = await this.draftService.getDraft(this.projectId, this.articleId);
    if (!draft) {
      throw new Error(`Article draft "${this.articleId}" was not found`);
    }

    this.draft = draft;
    const flowDocument = draft.flowDocument;
    this.launchScreenId = flowDocument.launchScreenId || null;
    this.screensGraphMetadata = (flowDocument.metadata as Record<string, unknown> | undefined) ?? null;
    this.variables = { ...((flowDocument.variables as Record<string, FunnelVariable> | undefined) ?? {}) };

    const steps = Object.values(flowDocument.screens ?? {}) as StepNode[];
    steps.forEach((step: StepNode) => {
      const flowStep = v2ScreenAdapter.toFlowEntity(step);
      v2ScreenRegistry.register(flowStep);
    });

    console.log(`[ArticleV2Coordinator] Loaded ${steps.length} screens, launchScreenId: ${this.launchScreenId}`);
  }

  setCanvasHandle(handle: ReactFlowCanvasHandle | null): void {
    this.canvasHandle = handle;
    if (handle && this.initialized) {
      this.loadExistingScreensToCanvas();
    }
  }

  setLoadingCallbacks(callbacks: LoadingCallbacks): void {
    this.loadingCallbacks = callbacks;
  }

  private async wrapWithLoading<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    try {
      this.loadingCallbacks?.onSyncStart?.(operation);
      const result = await fn();
      this.loadingCallbacks?.onSyncEnd?.();
      return result;
    } catch (error) {
      this.loadingCallbacks?.onSyncError?.(error as Error, operation);
      this.loadingCallbacks?.onSyncEnd?.();
      throw error;
    }
  }

  // ============================================================================
  // WHOLE-DOCUMENT PERSISTENCE (serialized)
  // ============================================================================

  /**
   * Rebuild the article FlowDocument from the registry + coordinator state.
   * The registry is the in-memory source of truth while the canvas is open;
   * launchScreenId/variables/metadata live on the coordinator.
   */
  private buildFlowDocument(): FlowDocument {
    if (!this.draft) {
      throw new Error('[ArticleV2Coordinator] Draft not loaded');
    }
    const base = this.draft.flowDocument;
    const screens: Record<string, StepNode> = {};
    for (const flowStep of v2ScreenRegistry.getAll()) {
      const step = v2ScreenAdapter.toFirebaseStep(flowStep);
      screens[step.id] = step;
    }
    return {
      ...base,
      launchScreenId: this.launchScreenId || base.launchScreenId,
      screens,
      variables: { ...this.variables } as FlowDocument['variables'],
      metadata: (this.screensGraphMetadata ?? base.metadata) as FlowDocument['metadata'],
    };
  }

  /**
   * Queue a whole-document save. Saves run strictly one-after-another; each
   * run persists the CURRENT registry/coordinator state. A failed save rejects
   * only its own caller — the queue stays alive and the next save writes the
   * full state, so no mutation is lost to an intermediate error.
   */
  private enqueueSave(operation: string): Promise<void> {
    const run = async (): Promise<void> => {
      if (!this.draftService || !this.draft) {
        throw new Error('[ArticleV2Coordinator] Coordinator destroyed or draft not loaded');
      }
      const doc = this.buildFlowDocument();
      const saved = await this.draftService.saveFlowDocument(this.projectId, this.articleId, doc);
      this.draft = saved;
    };
    const op = this.saveTail.then(run);
    this.saveTail = op.then(() => undefined, () => undefined);
    return this.wrapWithLoading(operation, () => op);
  }

  /** Resolves when every queued save has settled (test/shutdown helper). */
  public async flushPendingSaves(): Promise<void> {
    await this.saveTail;
  }

  // ============================================================================
  // LAUNCH SCREEN
  // ============================================================================

  async getLaunchEntityId(): Promise<string | null> {
    if (this.launchScreenId) return this.launchScreenId;
    if (!this.draft && this.draftService) {
      const draft = await this.draftService.getDraft(this.projectId, this.articleId);
      if (draft) {
        this.draft = draft;
        this.launchScreenId = draft.flowDocument.launchScreenId || null;
      }
    }
    return this.launchScreenId;
  }

  async setLaunchEntity(entityId: string): Promise<{
    deletedTransitionIds: string[];
    affectedEntitiesBackup: Map<string, FlowStepNode>;
  }> {
    const previousLaunchId = this.launchScreenId;

    const step = v2ScreenRegistry.get(entityId);
    if (!step) throw new Error(`Screen not found: ${entityId}`);

    // Launch screen cannot have incoming transitions — delete and back them up
    const allTransitions = this.computeAllTransitions();
    const incomingTransitions = allTransitions.filter(t => t.targetEntityId === entityId);
    const deletedTransitionIds = incomingTransitions.map(t => t.id);

    const affectedEntitiesBackup = new Map<string, FlowStepNode>();
    for (const transition of incomingTransitions) {
      const sourceStep = v2ScreenRegistry.get(transition.sourceEntityId);
      if (sourceStep) {
        affectedEntitiesBackup.set(transition.sourceEntityId, JSON.parse(JSON.stringify(sourceStep)));
      }
    }

    if (deletedTransitionIds.length > 0) {
      await this.deleteTransitions(deletedTransitionIds);
    }

    this.launchScreenId = entityId;
    this.onLaunchScreenChanged?.(entityId);

    if (this.canvasHandle) {
      if (previousLaunchId && previousLaunchId !== entityId) {
        this.canvasHandle.updateNodeData(previousLaunchId, { isLaunchScreen: false });
      }
      this.canvasHandle.updateNodeData(entityId, { isLaunchScreen: true });
    }

    await this.enqueueSave('Setting launch screen');
    return { deletedTransitionIds, affectedEntitiesBackup };
  }

  // ============================================================================
  // FLOWCOORDINATOR INTERFACE
  // ============================================================================

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async initialize(_projectId?: string, _articleId?: string, launchScreenId?: string): Promise<void> {
    if (launchScreenId) this.launchScreenId = launchScreenId;
  }

  getAllEntities(): FlowStepNode[] {
    return v2ScreenRegistry.getAll();
  }

  private generateUniqueName(baseName: string): string {
    const allEntities = v2ScreenRegistry.getAll();
    const usedNames = new Set(allEntities.map(e => e.name || ''));
    if (!usedNames.has(baseName)) return baseName;
    let counter = 2;
    while (usedNames.has(`${baseName} ${counter}`)) counter++;
    return `${baseName} ${counter}`;
  }

  async createEntity(data: Partial<FlowStepNode>, position: { x: number; y: number }): Promise<CreateEntityResult<FlowStepNode>> {
    const isFirstScreen = v2ScreenRegistry.getAll().length === 0;
    const templateId = (data as any).templateId || (data as any).id;

    let newStep: FlowStepNode | null = null;

    if (templateId && this.templateSource) {
      const template = this.templateSource(templateId);
      if (template) {
        newStep = v2ScreenAdapter.createFromV2Template(template, position);
      }
    }

    if (!newStep) {
      if (!('content' in data) || typeof data.screenType !== 'string') {
        throw new Error('Article V2 screen creation requires a native V2 StepNode template');
      }
      newStep = v2ScreenAdapter.createFromV2Template(data as StepNode, position);
    }

    // The article template panel is FlexibleLayout-only; anything else here
    // means a mis-wired template source — keep the screen but make it loud.
    if (newStep.screenType !== 'FlexibleLayout') {
      console.warn('[ArticleV2Coordinator] Non-FlexibleLayout template on article canvas:', newStep.screenType);
    }

    newStep.name = this.generateUniqueName(newStep.name || 'Article screen');
    this.addStepToCanvas(newStep);
    await this.enqueueSave('Creating screen');
    if (isFirstScreen) await this.setLaunchEntity(newStep.entityId);
    return { entity: newStep };
  }

  updateEntity(entityId: string, updates: Partial<FlowStepNode>): void {
    const current = v2ScreenRegistry.get(entityId);
    if (!current) return;
    v2ScreenRegistry.update(entityId, { ...current, ...updates });
  }

  async restoreEntities(steps: FlowStepNode[], affectedStepsBackup?: Map<string, FlowStepNode>): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);
    }

    if (affectedStepsBackup && affectedStepsBackup.size > 0) {
      for (const [stepId, originalStep] of affectedStepsBackup) {
        v2ScreenRegistry.update(stepId, originalStep);
      }
    }

    await this.enqueueSave(`Restoring ${steps.length} screen(s)`);

    const restoredTransitions = this.computeTransitionsFromSteps(steps);
    this.syncEdgesToCanvas(restoredTransitions);
  }

  async deleteEntities(stepIds: string[]): Promise<{
    deletedTransitionIds: string[];
    affectedScreensBackup: Map<string, FlowStepNode>;
  }> {
    const allTransitions = this.computeAllTransitions();
    const deletedTransitionIds = allTransitions
      .filter(t => stepIds.includes(t.sourceEntityId) || stepIds.includes(t.targetEntityId))
      .map(t => t.id);

    const affectedScreensBackup = this.cleanupIncomingConnections(stepIds);

    stepIds.forEach(id => v2ScreenRegistry.delete(id));
    this.executeCanvasOperation(handle => handle.removeNodes(stepIds));
    this.syncEdgesToCanvas();

    // The document must keep a valid launch pointer: deleting the launch node
    // reassigns launch to the first remaining screen (publish validation and
    // the runtime both resolve screens[launchScreenId]).
    if (this.launchScreenId && stepIds.includes(this.launchScreenId)) {
      const remaining = v2ScreenRegistry.getAll();
      const nextLaunchId = remaining[0]?.entityId ?? null;
      if (nextLaunchId) {
        this.launchScreenId = nextLaunchId;
        this.onLaunchScreenChanged?.(nextLaunchId);
        this.executeCanvasOperation(handle => handle.updateNodeData(nextLaunchId, { isLaunchScreen: true }));
      }
    }

    await this.enqueueSave(`Deleting ${stepIds.length} screen(s)`);

    return { deletedTransitionIds, affectedScreensBackup };
  }

  async updateEntityPositions(moves: EntityMove[]): Promise<void> {
    const normalizedMoves = moves.map(move => ({
      entityId: move.entityId,
      position: { x: move.newPosition.x ?? 0, y: move.newPosition.y ?? 0 }
    }));

    normalizedMoves.forEach(({ entityId, position }) => {
      v2ScreenRegistry.updatePosition(entityId, position);
    });

    await this.enqueueSave(`Moving ${moves.length} screen(s)`);

    this.executeCanvasOperation(handle =>
      handle.updateNodePositions(moves.map(m => ({
        entityId: m.entityId,
        oldPosition: m.oldPosition,
        newPosition: m.newPosition
      })))
    );
  }

  async cloneEntity(sourceEntityId: string, offset: PositionOnConfigurator): Promise<CreateEntityResult<FlowStepNode>> {
    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) throw new Error(`Screen not found: ${sourceEntityId}`);

    const newPosition = {
      x: (sourceStep.position?.x || 0) + (offset.x ?? 0),
      y: (sourceStep.position?.y || 0) + (offset.y ?? 0)
    };

    const cloned = JSON.parse(JSON.stringify(sourceStep)) as FlowStepNode;
    const withoutEdges = clearAllStepEdges(cloned as StepNode);
    const uuid = crypto.randomUUID();

    const clonedStep: FlowStepNode = {
      ...(withoutEdges as FlowStepNode),
      id: uuid, entityId: uuid, position: newPosition,
      name: this.generateUniqueName(`${sourceStep.name || 'Article screen'} (Copy)`),
    };

    this.addStepToCanvas(clonedStep);
    await this.enqueueSave('Cloning screen');
    return { entity: clonedStep };
  }

  async addPreparedScreens(steps: FlowStepNode[]): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);
    }
    await this.enqueueSave(`Pasting ${steps.length} screen(s)`);
    this.syncEdgesToCanvas();
  }

  async updateEntityAndRefreshConnections(entityId: string, updatedStep: FlowStepNode): Promise<void> {
    v2ScreenRegistry.update(entityId, updatedStep);

    await this.enqueueSave('Updating screen');

    const handles = buildV2ActionHandles(updatedStep);
    this.executeCanvasOperation(handle => handle.updateNodeData(entityId, { handles }));

    const allEdges = this.computeAllTransitions();
    this.executeCanvasOperation(handle => handle.syncEdges(allEdges));
  }

  // ============================================================================
  // TRANSITION OPERATIONS
  // ============================================================================

  async createTransition(input: CreateTransitionInput): Promise<EntityTransition> {
    const { sourceEntityId, targetEntityId, sourceActionKey, label } = input;
    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) throw new Error(`Source screen not found: ${sourceEntityId}`);
    if (!v2ScreenRegistry.has(targetEntityId)) throw new Error(`Target screen not found: ${targetEntityId}`);
    if (sourceEntityId === targetEntityId) throw new Error('Cannot create transition to same screen');

    const transitionId = TransitionIdGenerator.generate(sourceEntityId, sourceActionKey, targetEntityId);
    const transition: EntityTransition = { id: transitionId, sourceEntityId, targetEntityId, sourceActionKey, label: label || '' };

    const updatedStepNode = v2TransitionManager.addTransition(sourceStep as StepNode, transition);
    v2ScreenRegistry.update(sourceEntityId, { ...updatedStepNode, entityId: sourceStep.entityId } as FlowStepNode);
    this.executeCanvasOperation(handle => handle.addEdge(this.createEdgeData(transition)));

    await this.enqueueSave('Creating transition');

    return transition;
  }

  async updateTransition(transitionId: string, updates: Partial<Omit<EntityTransition, 'id'>>): Promise<EntityTransition> {
    const found = this.findTransitionOwner(transitionId);
    if (!found) throw new Error(`Transition not found: ${transitionId}`);

    const { step: ownerStep, transition: existingTransition } = found;
    const updatedStepNode = v2TransitionManager.updateTransition(ownerStep as StepNode, transitionId, updates);
    v2ScreenRegistry.update(ownerStep.entityId, { ...updatedStepNode, entityId: ownerStep.entityId } as FlowStepNode);

    const updatedTransition: EntityTransition = { ...existingTransition, ...updates };
    this.executeCanvasOperation(handle => handle.updateEdge(this.createEdgeData(updatedTransition)));

    await this.enqueueSave('Updating transition');

    return updatedTransition;
  }

  async deleteTransition(transitionId: string): Promise<void> {
    await this.deleteTransitions([transitionId]);
  }

  async deleteTransitions(transitionIds: string[]): Promise<void> {
    const stepUpdates = new Map<string, FlowStepNode>();
    const allSteps = v2ScreenRegistry.getAll();

    for (const transitionId of transitionIds) {
      for (const step of allSteps) {
        const currentStep = stepUpdates.get(step.entityId) || step;
        const transitions = v2TransitionManager.extractTransitions(currentStep as StepNode);
        if (transitions.some(t => t.id === transitionId)) {
          const updatedStepNode = v2TransitionManager.removeTransition(currentStep as StepNode, transitionId);
          stepUpdates.set(step.entityId, { ...updatedStepNode, entityId: step.entityId } as FlowStepNode);
          break;
        }
      }
    }

    stepUpdates.forEach((step, entityId) => v2ScreenRegistry.update(entityId, step));
    this.executeCanvasOperation(handle => handle.removeEdges(transitionIds));

    await this.enqueueSave(`Deleting ${transitionIds.length} transition(s)`);
  }

  getAllTransitions(): EntityTransition[] { return this.computeAllTransitions(); }
  getTransition(transitionId: string): EntityTransition | undefined {
    return this.computeAllTransitions().find(t => t.id === transitionId);
  }

  // ============================================================================
  // FLOW VARIABLES (FlowVariablesHost capability — flowDocument.variables)
  // ============================================================================

  async getFlowVariables(): Promise<Record<string, FunnelVariable>> {
    return { ...this.variables };
  }

  async saveFlowVariable(variable: FunnelVariable): Promise<void> {
    this.variables = { ...this.variables, [variable.id]: variable };
    await this.enqueueSave('Saving variable');
  }

  async deleteFlowVariable(variableId: string): Promise<void> {
    const next = { ...this.variables };
    delete next[variableId];
    this.variables = next;
    await this.enqueueSave('Deleting variable');
  }

  // ============================================================================
  // ACCESSORS
  // ============================================================================

  getCanvasHandle(): ReactFlowCanvasHandle | null { return this.canvasHandle; }
  getEntity(id: string): FlowStepNode | undefined { return v2ScreenRegistry.get(id); }
  getScreen(screenId: string): FlowStepNode | undefined { return v2ScreenRegistry.get(screenId); }
  isInitialized(): boolean { return this.initialized; }

  destroy(): void {
    if (!this.draftService) return;
    console.log('[ArticleV2Coordinator] Destroying...');
    v2ScreenRegistry.clear();
    this.canvasHandle = null;
    this.draftService = null;
    this.draft = null;
    this.onLaunchScreenChanged = undefined;
    this.initialized = false;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private loadExistingScreensToCanvas(): void {
    if (!this.canvasHandle) return;

    const stepIds = v2ScreenRegistry.getAllIds();
    stepIds.forEach((stepId) => {
      const step = v2ScreenRegistry.get(stepId);
      if (!step) return;
      const handles = buildV2ActionHandles(step);
      this.canvasHandle!.addNode({
        entityId: step.entityId, entityType: 'screen' as const,
        nodeType: 'onboarding-screen-v2' as const,
        position: step.position || { x: 0, y: 0 },
        handles, isLaunchScreen: step.entityId === this.launchScreenId,
      });
    });

    const allEdges = this.computeAllTransitions();
    this.canvasHandle!.syncEdges(allEdges);
  }

  private addStepToCanvas(step: FlowStepNode): void {
    v2ScreenRegistry.register(step);
    if (this.canvasHandle) {
      const handles = buildV2ActionHandles(step);
      this.canvasHandle.addNode({
        entityId: step.entityId, entityType: 'screen',
        nodeType: 'onboarding-screen-v2',
        position: step.position || { x: 0, y: 0 },
        handles, isLaunchScreen: step.entityId === this.launchScreenId,
      });
    }
  }

  private syncEdgesToCanvas(edges?: EntityTransition[]): void {
    if (this.canvasHandle) {
      this.canvasHandle.syncEdges(edges ?? this.computeAllTransitions());
    }
  }

  private computeTransitionsFromSteps(steps: FlowStepNode[]): EntityTransition[] {
    const transitions: EntityTransition[] = [];
    steps.forEach(step => transitions.push(...v2TransitionManager.extractTransitions(step as StepNode)));
    return transitions;
  }

  private computeAllTransitions(): EntityTransition[] {
    return this.computeTransitionsFromSteps(v2ScreenRegistry.getAll());
  }

  private executeCanvasOperation<T>(fn: (handle: ReactFlowCanvasHandle) => T): T | void {
    if (!this.canvasHandle) return;
    return fn(this.canvasHandle);
  }

  private findTransitionOwner(transitionId: string): { step: FlowStepNode; transition: EntityTransition } | undefined {
    for (const step of v2ScreenRegistry.getAll()) {
      const transitions = v2TransitionManager.extractTransitions(step as StepNode);
      const transition = transitions.find(t => t.id === transitionId);
      if (transition) return { step, transition };
    }
    return undefined;
  }

  /**
   * Remove transitions that point INTO the screens being deleted from their
   * surviving source screens (registry-only — the following whole-doc save
   * persists everything in one write).
   */
  private cleanupIncomingConnections(deletedStepIds: string[]): Map<string, FlowStepNode> {
    const affectedStepsBackup = new Map<string, FlowStepNode>();
    const allTransitions = this.computeAllTransitions();
    const incomingTransitions = allTransitions.filter(t =>
      deletedStepIds.includes(t.targetEntityId) && !deletedStepIds.includes(t.sourceEntityId)
    );

    const transitionsBySource = new Map<string, string[]>();
    for (const transition of incomingTransitions) {
      if (!transitionsBySource.has(transition.sourceEntityId)) {
        transitionsBySource.set(transition.sourceEntityId, []);
      }
      transitionsBySource.get(transition.sourceEntityId)!.push(transition.id);
    }

    for (const [sourceStepId, transitionIds] of transitionsBySource) {
      const step = v2ScreenRegistry.get(sourceStepId);
      if (!step) continue;

      affectedStepsBackup.set(sourceStepId, JSON.parse(JSON.stringify(step)));

      let currentStep = step as StepNode;
      for (const transitionId of transitionIds) {
        currentStep = v2TransitionManager.removeTransition(currentStep, transitionId);
      }
      v2ScreenRegistry.update(sourceStepId, { ...currentStep, entityId: step.entityId } as FlowStepNode);
    }

    return affectedStepsBackup;
  }

  private createEdgeData(transition: EntityTransition): EdgeData {
    return {
      id: transition.id,
      source: transition.sourceEntityId,
      target: transition.targetEntityId,
      sourceHandle: TransitionIdGenerator.generateHandleId(transition.sourceEntityId, transition.sourceActionKey),
      label: transition.label ?? '',
      type: 'transition',
      animated: false,
      data: {
        sourceEntityId: transition.sourceEntityId,
        targetEntityId: transition.targetEntityId,
        sourceActionKey: transition.sourceActionKey,
        rules: transition.rules || [],
      }
    };
  }

  debug(): void {
    console.log('[ArticleV2Coordinator] Debug:');
    console.log(`  projectId: ${this.projectId}`);
    console.log(`  articleId: ${this.articleId}`);
    console.log(`  launchScreenId: ${this.launchScreenId}`);
    const transitions = this.computeAllTransitions();
    console.log(`  Transitions: ${transitions.length}`);
    v2ScreenRegistry.debug();
  }
}
